import Cookies from 'mj-cookie'
import moment from 'mj-moment'
import EventModel from '@/models/events'
import UserModel from '@/models/users'
import SearchForm from '@/utils/search/SearchForm'
import SearchParams from '@/utils/search/SearchParams'
import GlobalCookie from '@/utils/GlobalCookie'
import sendOpixEvent from '@/utils/sendOpixEvent'
import { buildQueryString } from '@/utils/url-action'
import { EXCLUSIVE_CONDITION_QUERY } from '@/utils/constants/query'
import { interestPurposesMap, interestSubListMap, interestSubListNameToSubItemsMap } from '@/utils/constants/purposes'

const DEFAULT_MODEL_DATA = {
  prefectures: [],
  region: null,
  areas: [],
  start_date: null,
  start_dates: [],
  end_date: null,
  week_days: [],
  user_gender: null,
  num_of_people: null,
  num_of_people_range: [],
  user_age: null,
  companion_age: null,
  purposes: [],
  content_types: [],
  sale_types: [],
  price_from: 0,
  price_to: 0,
  exclude_sold_out: 1,
  organizers: [],
  keyword: null,
  features: [],
  fixed: null,
  has_stage_discount_tickets: null,
  has_mj_select: null,
  from_time: '',
  syumiplusPurposes: [],
  interestPurposes: [],
  onlineEventTypes: [],
  address_venue: []
}

// Fields to skip when EXCLUSIVE_CONDITION_QUERY exists
const EXCLUSIVE_CONDITION_SKIP_FIELDS = ['prefectures', 'region', 'areas']

export default {
  data() {
    return {
      totalCount: 0,
      fetchedCountQuery: null,
      model: {
        ...DEFAULT_MODEL_DATA
      }
    }
  },
  computed: {
    listPurposes() {
      return EventModel.getListPurposes()
    },
    searchForm() {
      return new SearchForm(this.$route.query)
    },
    searchQueryParams() {
      return this.searchForm.convertFormDataToQueryParams(this.model)
    },
    searchApiQueryString() {
      const searchFormParams = { ...this.searchQueryParams, only_count: 1 }
      const searchParams = new SearchParams(this.$store, this.$route, { searchFormParams })
      return searchParams.searchApiQueryString
    },
    // TODO 6078 refactoring this function
    // purposes や interestPurposes が Object なので、
    // それぞれの key に対して、slug を取得して配列に変換する
    fetchCountQueryParams() {
      const copyModel = { ...this.model }
      copyModel.interestPurposes = copyModel.interestPurposes.map(purpose => purpose)
      copyModel.purposes = copyModel.purposes.filter(purpose => purpose !== 'interest').map(purpose => purpose)
      return this.searchForm.convertFormDataToQueryParams(copyModel)
    },
    fetchCountApiQueryString() {
      const searchFormParams = { ...this.fetchCountQueryParams, only_count: 1 }
      const searchParams = new SearchParams(this.$store, this.$route, { searchFormParams })
      return searchParams.searchApiQueryString
    },
    canUseKonkatsuFreePass() {
      const globalCookie = new GlobalCookie(this.$store)
      const userGender = this.$store.myGender || globalCookie.get('gender')
      return userGender === 'female'
    },
    userDefaultGender() {
      return this.$store.myGender || Cookies.get('gender')
    },
    userDefaultAge() {
      return this.$store.myAge || Cookies.get('age')
    },
    isCheckedConditions() {
      return (
        (!this.$store.isLoggedIn && (this.model.user_gender !== null || this.model.user_age !== null)) ||
        this.model.num_of_people !== null ||
        this.model.num_of_people_range?.length ||
        this.model.companion_age !== null ||
        this.model.price_from !== 0 ||
        this.model.price_to !== 0 ||
        this.model.keyword !== null ||
        this.model.fixed ||
        this.model.has_stage_discount_tickets ||
        this.model.has_mj_select ||
        this.model.onlineEventTypes.length ||
        this.model.content_types?.length ||
        this.model.address_venue?.length
      )
    },
    checkedConditionsCount() {
      const arrayConditions = ['num_of_people_range', 'content_types', 'address_venue', 'onlineEventTypes']
      const booleanConditions = ['fixed', 'has_stage_discount_tickets', 'has_mj_select']
      let count = 0

      if (!this.$store.isLoggedIn) {
        if (this.model.user_gender !== null) count++
        if (this.model.user_age !== null) count++
      }
      if (this.model.keyword !== null) count++
      if (this.model.companion_age !== null) count++
      if (this.model.price_from !== 0 || this.model.price_to !== 0) count++

      arrayConditions.forEach(key => {
        if (this.model[key]?.length) count += this.model[key].length
      })
      booleanConditions.forEach(key => {
        if (this.model[key]) count++
      })

      return count
    },
    selectedPurposeCount() {
      const filteredPurposes = this.model.purposes.filter(purpose => !['interest', 'same_hobbies'].includes(purpose))
      return (
        filteredPurposes.length +
        (this.model.interestPurposes && this.model.interestPurposes.length > 0 ? 1 : 0) +
        (this.model.features && this.model.features.includes('counseling') ? 1 : 0)
      )
    },
    /**
     * @returns {string[] | null}
     */
    interestPurposesFromRoute() {
      const pageDetector = new PageDetector(this.$route)

      if (!pageDetector.isInterestsPage) {
        return null
      }

      const interest = this.$route.params.interest
      const subInterest = this.$route.params.sub_interest

      const allInterests = Array.from(interestSubListMap.keys())
      const allSubInterests = Array.from(interestPurposesMap.keys())

      const isInterestValid = interest && allInterests.includes(interest)
      const isSubInterestValid = subInterest && allSubInterests.includes(subInterest)

      if (isInterestValid && isSubInterestValid) {
        return [subInterest]
      }

      if (isInterestValid && !subInterest) {
        return interestSubListNameToSubItemsMap.get(interest)?.map(item => item.slug) || []
      }

      if (!isInterestValid && interest) {
        return []
      }

      if (!interest && !subInterest) {
        return allSubInterests
      }

      if (!isSubInterestValid && subInterest) {
        return []
      }

      return []
    }
  },
  watch: {
    // this watching will be called when router or model changing
    searchApiQueryString() {
      this.fetchCount()
    }
  },
  created() {
    this.initializeData()
  },
  mounted() {
    this.fetchCount()
  },
  methods: {
    getWdayName(wday) {
      const listOfWeekdays = moment.weekdaysShort()
      return listOfWeekdays[wday - 1]
    },

    /**
     * Init search condition data.
     */
    initializeData() {
      this.buildConditionData()
      if (typeof this.afterInitializeData === 'function') this.afterInitializeData()

      // onlineEventTypes条件は「online」値のみを入れられる仕様になったため、複数時はonlineEventTypesの条件を外す。
      if (this.model.onlineEventTypes.length > 1) this.model.onlineEventTypes = []

      // Reset exclusive condition fields to default values if exclusive_condition exists
      const hasExclusiveCondition = Object.prototype.hasOwnProperty.call(this.$route.query, EXCLUSIVE_CONDITION_QUERY)
      if (hasExclusiveCondition) {
        EXCLUSIVE_CONDITION_SKIP_FIELDS.forEach(field => {
          if (DEFAULT_MODEL_DATA[field] !== undefined) {
            this.model[field] = JSON.parse(JSON.stringify(DEFAULT_MODEL_DATA[field]))
          }
        })
      }
    },

    /**
     * Build search condition model data from query params
     */
    buildConditionData() {
      const formData = this.searchForm.mapQueryToFormData
      Object.keys(formData).forEach(key => {
        this.model[key] = formData[key]
      })
      if (this.userDefaultGender) this.model.user_gender = UserModel.getGenderValue(this.userDefaultGender)
      if (this.userDefaultAge) this.model.user_age = this.userDefaultAge

      if (this.interestPurposesFromRoute) {
        this.model.interestPurposes = this.interestPurposesFromRoute
      }
    },
    fetchCount() {
      // TODO 6078の refactoring あと、元に戻すこと
      // const queryString = this.searchApiQueryString
      const queryString = this.fetchCountApiQueryString
      if (this.fetchedCountQuery === queryString) return
      this.fetchedCountQuery = queryString
      this.$store
        .apiGet(`events/${queryString}`)
        .then(res => {
          this.totalCount = res.data.count
        })
        .catch(() => {
          this.fetchedCountQuery = null
        })
    },
    clearConditions() {
      Object.keys(DEFAULT_MODEL_DATA).forEach(key => {
        this.model[key] = DEFAULT_MODEL_DATA[key]
      })
      if (typeof this.afterClearConditions === 'function') this.afterClearConditions()
    },
    setFormDataToCookie() {
      if (this.model.user_age) {
        Cookies.set('age', this.model.user_age)
      } else {
        Cookies.remove('age')
      }
      if (this.model.user_gender) {
        Cookies.set('gender', UserModel.findGenderByValue(this.model.user_gender))
      } else {
        Cookies.remove('gender')
      }
    },
    toSearchResultPage() {
      if (!this.$store.isLoggedIn) this.setFormDataToCookie()
      if (Object.keys(this.searchQueryParams).length === 0) this.searchQueryParams['exclude-sold-out'] = 1
      this.model.purposes = this.model.purposes.filter(purpose => !['interest', 'same_hobbies'].includes(purpose))
      if (this.storeSearchData) {
        this.storeSearchData(this.model)
      }
      // Add and update search_form_version to identify SpSearch submitting request and force fetching data again
      const searchFormVersion = this.$route.query.search_form_version ? parseInt(this.$route.query.search_form_version) + 1 : 1
      if (this.searchQueryParams.s) {
        this.$store.dispatch('createOrUpdateKeywordSuggestion', { keyword: this.searchQueryParams.s })
      }
      this.$router.push({ name: 'search', query: { ...this.searchQueryParams, search_form_version: searchFormVersion } })
      if (this.model.keyword) {
        const opixDatas = {
          userID: (this.$store.$state.my_profile && this.$store.$state.my_profile.id) || null,
          currentRoute: import.meta.env.ROOT_URL + `/search/${buildQueryString(this.searchQueryParams)}`,
          lastRoute: import.meta.env.ROOT_URL + this.$route.fullPath,
          free_word: this.model.keyword
        }
        sendOpixEvent('free_word_search', opixDatas)
      }
      if (typeof this.afterRedirectToResultPage === 'function') this.afterRedirectToResultPage()
    },
    listAreasHasEventsByPrefecture(listAreasByPrefecture, eventCountInArea) {
      return listAreasByPrefecture.filter(area => eventCountInArea(area.id) > 0)
    }
  }
}
